/**
 * Classic mode (04 §1, 01 §2): the 1987 structure. A persistent six-slot roster, Assign and
 * Retire, free pod packing within the caps, and merit, rank and medals only - no perks,
 * traits, requisition, readiness or wound carry-over.
 */

import { isOriginalArchetype, type Loadout, type MissionParams } from '../sim/types';
import { giveMedal, awardMedals } from './aftermath/medals';
import { applyMerit } from './aftermath/merit';
import { toMemorial } from './aftermath/roster';
import type { Deployment } from './api';
import { blankRecord, cleanName } from './company';
import { archetype, BIOME_NAMES } from './content/archetypes';
import { CAPS, COMPANY, STANDARD_ISSUE } from './content/economy';
import { withoutDogs } from './dogs';
import { cleanGear } from './gear';
import { priceLoadout } from './loadout';
import { recordFlight } from './records';
import { type FullSummary, fullName, newRangerId, type ProfileState, snapshot } from './state';
import type { Aftermath, RangerRecord } from './types';

function rosterRanger(profile: ProfileState, rangerId: string): RangerRecord {
  const ranger = profile.classicRoster.find((r) => r.id === rangerId);
  if (!ranger) throw new Error('That ranger is not on the roster.');
  return ranger;
}

/** Assign: a new PFC in a free slot. */
export function recruitClassic(
  profile: ProfileState, seed: number, firstName: string, surname: string, callsign: string,
): RangerRecord {
  if (profile.classicRoster.length >= COMPANY.classicSlots) {
    throw new Error(`The roster holds ${COMPANY.classicSlots} rangers: retire one first.`);
  }
  const ranger = blankRecord(newRangerId(profile), seed >>> 0, cleanName(firstName, 'first name'),
    cleanName(surname, 'surname'), cleanName(callsign, 'callsign').toUpperCase());
  profile.classicRoster.push(ranger);
  return ranger;
}

/** Retire: frees the slot. The living go to the roll of honour; a captive left behind is lost. */
export function retireClassic(profile: ProfileState, rangerId: string): void {
  const ranger = rosterRanger(profile, rangerId);
  profile.classicRoster = profile.classicRoster.filter((r) => r.id !== rangerId);
  if (ranger.status === 'pow') {
    toMemorial(profile, null, ranger, 'lost', 'P.O.W. - never recovered', 'Struck from the roster while a prisoner');
  } else if (ranger.status !== 'kia') profile.retired.push({ ...ranger, status: 'retired' });
}

export function deployClassic(
  profile: ProfileState, params: MissionParams, rangerId: string, loadout: Loadout,
): Deployment {
  const ranger = rosterRanger(profile, rangerId);
  if (ranger.status !== 'ready') throw new Error(`${fullName(ranger)} cannot deploy (${ranger.status}).`);
  // Classic is the 1987 game: the expansion's missions fly in Operations, Practice and the Daily Drop (10 intro).
  if (!isOriginalArchetype(params.archetype)) throw new Error('Classic mode flies the twelve missions of 1987.');
  // There is no operation stock to draw on, and 1987 had none of it.
  if (cleanGear(loadout.gear)) throw new Error('Classic mode issues the 1987 kit only: no special equipment.');
  priceLoadout(loadout, { carryCap: CAPS.carry, maxPods: CAPS.pods, standard: STANDARD_ISSUE, free: true });
  const flown: Loadout = { carried: snapshot(loadout.carried), pods: snapshot(loadout.pods) };
  // ROUND8: 1987 had no dogs (01 §7) and neither does Classic, whatever the parameters say.
  return { params: snapshot(withoutDogs(params)), rangerId, loadout: flown, mods: {}, mode: 'classic' };
}

export function reportClassic(profile: ProfileState, deployment: Deployment, summary: FullSummary): Aftermath {
  const ranger = rosterRanger(profile, deployment.rangerId);
  const { params } = deployment;
  const result: Aftermath = {
    ranger, meritGained: 0, rpGained: 0, promotedTo: null, perkChoices: null, medals: [], readinessChange: 0,
    readinessReasons: [], freedRangers: [], memorial: null, operationStatus: profile.operation?.status ?? 'active',
  };
  // Never jumped: returned to base with a reprimand, nothing recorded (01 §2).
  if (summary.outcome === 'scrubbed') return snapshot(result);

  const success = summary.outcome === 'extracted' && summary.objectiveComplete;
  ranger.record.missions += 1;
  ranger.record.successes += success ? 1 : 0;
  ranger.record.kills += summary.kills;
  ranger.record.knifeKills += summary.knifeKills;
  ranger.record.woundsTaken += summary.woundsAtEnd;
  const best = profile.bestScores[params.archetype] ?? 0;
  if (success && summary.score > best) profile.bestScores[params.archetype] = summary.score;

  // Completing mission 8 returns every captured veteran on the roster to duty, as in 1987.
  if (summary.freedPows && params.archetype === 'pow-camp') {
    result.freedRangers = profile.classicRoster.filter((r) => r.status === 'pow');
    for (const freed of result.freedRangers) {
      freed.status = 'ready';
      giveMedal(freed, 'pow-medal');
    }
  }
  result.medals = awardMedals({
    ranger, summary, archetype: params.archetype, mode: 'classic', freed: result.freedRangers.length, landings: 0,
  });
  result.meritGained = Math.max(0, Math.round(summary.score));
  result.promotedTo = applyMerit(ranger, result.meritGained, false, false);

  const mission = `${archetype(params.archetype).name} (${BIOME_NAMES[params.biome]})`;
  if (summary.outcome === 'kia') {
    result.memorial = toMemorial(
      profile, null, ranger, 'kia', mission, summary.cause ?? 'Killed in action', summary.photo,
    );
  } else if (summary.outcome === 'captured') {
    ranger.status = 'pow';
    ranger.record.timesCaptured += 1;
  } else if (summary.outcome === 'walkedOut' && giveMedal(ranger, 'evaders-ribbon', false)) {
    result.medals.push('evaders-ribbon');
  }

  // Filed last of all: his medals, his promotion and his fate are what a service file is read for,
  // and a man who walked out earns his ribbon in the block above.
  recordFlight(profile, {
    archetype: params.archetype, rangerId: ranger.id, success, secondsUsed: summary.secondsUsed,
    entry: {
      operation: 'CLASSIC', mission, archetype: params.archetype, threat: summary.threat,
      outcome: summary.outcome, objectiveComplete: summary.objectiveComplete, score: summary.score,
      kills: summary.kills, wounds: summary.woundsAtEnd, medals: [...result.medals],
      ...(result.promotedTo === null ? {} : { promotedTo: result.promotedTo }),
    },
  });
  return snapshot(result);
}
